import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Loader2, Newspaper, Presentation, type LucideIcon } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  ErrorBanner,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@lumanity/ui/primitives";
import { useCreateOutput, useKantataProjectTasks, useOutputTypes, type KantataTask } from "./outputsApi";
import { outputPath, wordRangeLabel } from "./model";
import type { OutputType } from "./types";

const TYPE_ICONS: Record<OutputType, LucideIcon> = {
  abstract: FileText,
  manuscript: Newspaper,
  poster: Presentation,
};

interface OutputPickerDialogProps {
  projectId: string;
  kantataProjectId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** "What would you like to write?" — pick a Kantata task and a publication type; creates the output and opens its Brief. */
const OutputPickerDialog = ({ projectId, kantataProjectId, open, onOpenChange }: OutputPickerDialogProps) => {
  const navigate = useNavigate();
  const [selectedTask, setSelectedTask] = useState<KantataTask | null>(null);
  const { data: types = [], isLoading: typesLoading, error: typesError } = useOutputTypes();
  const { data: tasks = [], isLoading: tasksLoading, error: tasksError } = useKantataProjectTasks(kantataProjectId, { enabled: open });
  const create = useCreateOutput(projectId);

  const handleSelect = (type: OutputType) =>
    create.mutate(
      {
        type,
        kantata_task_id: selectedTask?.id ?? "",
        kantata_task_title: selectedTask?.title ?? null,
        kantata_task_status: selectedTask?.status ?? null,
      },
      {
        onSuccess: (output) => {
          onOpenChange(false);
          navigate(outputPath(projectId, output.id));
        },
        onError: (e) => toast({ title: "Could not create the output", description: e.message, variant: "destructive" }),
      },
    );

  return (
    <Dialog open={open} onOpenChange={(next) => !create.isPending && onOpenChange(next)}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-2xl text-brand-navy">What would you like to write?</DialogTitle>
          <DialogDescription>
            Choose a publication type you are going to write. You can create multiple outputs within the same project and assign
            each of them to a unique Kantata task.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 max-w-sm">
          <Label htmlFor="output-task-code" className="font-semibold text-brand-navy">
            Task code (Kantata)<span className="text-destructive">*</span>
          </Label>
          <Select
            value={selectedTask?.id ?? ""}
            onValueChange={(id) => setSelectedTask(tasks.find((t) => t.id === id) ?? null)}
            disabled={!kantataProjectId || tasksLoading || create.isPending}
          >
            <SelectTrigger id="output-task-code">
              <SelectValue placeholder={tasksLoading ? "Loading tasks..." : "Select a task"} />
            </SelectTrigger>
            <SelectContent>
              {tasks.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  <span className="flex items-center gap-2">
                    <span>{t.title ?? t.id}</span>
                    {t.status && (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                        {t.status}
                      </span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!kantataProjectId && (
            <p className="text-xs text-muted-foreground">No Kantata project linked — add one in the project settings.</p>
          )}
          {kantataProjectId && !tasksLoading && !tasksError && tasks.length === 0 && (
            <p className="text-xs text-muted-foreground">No tasks found in the linked Kantata project.</p>
          )}
        </div>

        {(typesError || tasksError) && <ErrorBanner message={(typesError ?? tasksError)!.message} />}

        {typesLoading ? (
          <div className="text-muted-foreground py-8 text-center">Loading output types...</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            {types.map((info) => {
              const Icon = TYPE_ICONS[info.type] ?? FileText;
              const creatingThis = create.isPending && create.variables?.type === info.type;
              return (
                <div key={info.type} className="flex flex-col border bg-card">
                  <div className="flex h-32 items-center justify-center bg-brand-light-teal">
                    <Icon className="w-12 h-12 text-brand-teal" strokeWidth={1.25} />
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-lg font-semibold text-brand-navy">{info.label}</h3>
                      <span className="rounded-full bg-brand-light-teal px-2.5 py-0.5 text-xs font-medium text-brand-navy whitespace-nowrap">
                        {wordRangeLabel(info.word_range)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Outline</p>
                      <div className="flex flex-wrap gap-1">
                        {info.outline.length === 0 && (
                          <span className="text-[11px] text-muted-foreground">No outline template set up yet</span>
                        )}
                        {info.outline.map((item) => (
                          <span key={item} className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button
                      className="w-full bg-brand-teal hover:bg-brand-teal/90 text-white"
                      disabled={!selectedTask || create.isPending}
                      onClick={() => handleSelect(info.type)}
                    >
                      {creatingThis && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                      Select
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OutputPickerDialog;
